import { isSsoEnabled } from '../auth/cognitoConfig';
import { getAuthToken, triggerAuthExpired } from '../auth/authToken';

export interface RuntimeApiConfig {
  apiUrl: string;
  token: string;
}

const API_URL_KEY = 'easypost_api_url';
const API_TOKEN_KEY = 'easypost_api_token';

export const API_CONFIG_CHANGED_EVENT = 'easypost:api-config-changed';
export const API_AUTH_REQUIRED_EVENT = 'easypost:api-auth-required';

export function getDefaultApiUrl(): string {
  const configuredUrl = import.meta.env.VITE_API_URL?.trim();
  if (configuredUrl) return normalizeApiUrl(configuredUrl);
  return '';
}

export function normalizeApiUrl(value: string): string {
  return value.trim().replace(/\/+$/, '');
}

export function isAllowedApiUrl(value: string): boolean {
  if (!value) return import.meta.env.DEV;
  try {
    const parsed = new URL(value);
    if (parsed.username || parsed.password || parsed.search || parsed.hash) return false;
    return parsed.protocol === 'https:' || (import.meta.env.DEV && parsed.protocol === 'http:');
  } catch {
    return false;
  }
}

export function getStoredApiUrl(): string {
  try {
    return normalizeApiUrl(sessionStorage.getItem(API_URL_KEY) ?? getDefaultApiUrl());
  } catch {
    return getDefaultApiUrl();
  }
}

export function getRuntimeApiConfig(): RuntimeApiConfig | null {
  try {
    const apiUrl = getStoredApiUrl();
    if (!isAllowedApiUrl(apiUrl)) return null;

    // In SSO mode the bearer is the Cognito access token owned by the OIDC
    // session, not a value stored in this module.
    const token = isSsoEnabled()
      ? getAuthToken() ?? ''
      : sessionStorage.getItem(API_TOKEN_KEY)?.trim() ?? '';

    if (!token) return null;

    return { apiUrl, token };
  } catch {
    return null;
  }
}

/**
 * Persist only the API URL (used by the SSO sign-in screen, where the bearer
 * comes from Cognito rather than a typed token).
 */
export function saveRuntimeApiUrl(url: string): string {
  const normalized = normalizeApiUrl(url);
  sessionStorage.setItem(API_URL_KEY, normalized);
  window.dispatchEvent(new Event(API_CONFIG_CHANGED_EVENT));
  return normalized;
}

export function saveRuntimeApiConfig(config: RuntimeApiConfig): RuntimeApiConfig {
  const normalized = {
    apiUrl: normalizeApiUrl(config.apiUrl),
    token: config.token.trim(),
  };

  sessionStorage.setItem(API_URL_KEY, normalized.apiUrl);
  sessionStorage.setItem(API_TOKEN_KEY, normalized.token);
  window.dispatchEvent(new Event(API_CONFIG_CHANGED_EVENT));
  return normalized;
}

export function clearRuntimeApiToken(): void {
  if (isSsoEnabled()) {
    // Drop the OIDC session; the SSO gate will show the sign-in screen again.
    triggerAuthExpired();
    window.dispatchEvent(new Event(API_AUTH_REQUIRED_EVENT));
    return;
  }
  try {
    sessionStorage.removeItem(API_TOKEN_KEY);
  } catch {
    // The gate will still be notified even if storage is unavailable.
  }
  window.dispatchEvent(new Event(API_AUTH_REQUIRED_EVENT));
}

export function requireRuntimeApiConfig(): RuntimeApiConfig {
  const config = getRuntimeApiConfig();
  if (!config) {
    window.dispatchEvent(new Event(API_AUTH_REQUIRED_EVENT));
    throw new Error('API connection is not configured.');
  }
  return config;
}
